import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from "@mui/material";
import { SlidersHorizontal, X } from "lucide-react";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Footer from "../../../shared/components/Footer";

const THRESHOLDS = [
  { key: "processTemperatureC", label: "Process Temperature", unit: "°C",  high: 80,   critical: 100  },
  { key: "airTemperatureC",     label: "Air Temperature",     unit: "°C",  high: 40,   critical: 45   },
  { key: "torque",              label: "Torque",              unit: "N·m", high: 75,   critical: 110  },
  { key: "toolWear",            label: "Tool Wear",           unit: "min", high: 150,  critical: 230  },
  { key: "rotationalSpeedLow",  label: "RPM — Low Bound",     unit: "RPM", high: 1000, critical: 600  },
  { key: "rotationalSpeedHigh", label: "RPM — High Bound",    unit: "RPM", high: 3000, critical: 3500 },
  { key: "nullRpmRatio",        label: "Sensor Dropout Ratio",unit: "%",   high: 40,   critical: 60   },
];

const SENSOR_KNOWLEDGE = {
  processTemperatureC: {
    sensor_description:
      "processTemperatureC is the coolant-circuit temperature measured at the sump return line. It reflects the net heat load from cutting operations. High threshold: 80°C. Critical threshold: 100°C.",
    what_happens_physically:
      "Elevated coolant temperature reduces viscosity, lowering lubrication effectiveness at cutting surfaces. Above 80°C thermal expansion of the spindle and workpiece affects dimensional accuracy. Above 100°C coolant can vaporise locally, causing flash corrosion on cast-iron surfaces and potential O-ring degradation in the coolant circuit.",
    typical_root_causes:
      "Primary cause: coolant pump flow rate below spec (clogged filter or failing pump). Secondary causes: heat exchanger/chiller fouled with swarf or scale; coolant concentration ratio too low (below 5%); chip conveyor blocked, causing swarf to re-circulate in the sump.",
    inspection_steps:
      "1. Check coolant level in sump — low level concentrates heat.\n2. Measure coolant flow rate at the nozzle against OEM spec.\n3. Inspect and clean the coolant filter basket.\n4. Check chiller setpoint and verify compressor is running.\n5. Refractometer-test coolant concentration (target 6–8%).\n6. Clear the chip conveyor and flush sump to remove swarf accumulation.\n7. Allow system to cool below 40°C before resuming full-rate machining.",
  },
  airTemperatureC: {
    sensor_description:
      "airTemperatureC is the ambient temperature inside the machine enclosure, measured near the electrical cabinet. High threshold: 40°C. Critical threshold: 45°C.",
    what_happens_physically:
      "Elevated enclosure temperature shortens servo amplifier and drive capacitor lifespan. Above 40°C most industrial servo drives derate output current, reducing available torque. At 45°C thermal shutdown of amplifiers and PLCs becomes likely. High ambient temperature also causes dimensional drift in the machine structure and affects ball-screw preload.",
    typical_root_causes:
      "Primary cause: enclosure cooling fan failed or filter mat blocked. Secondary causes: HVAC system for the machine shop inadequate or under maintenance; enclosure door left open allowing hot shop air in; nearby heat source (e.g. another machine, hydraulic power unit) radiating into the enclosure.",
    inspection_steps:
      "1. Check enclosure cooling fan operation — confirm it spins and airflow is felt at the exhaust vent.\n2. Inspect and clean the filter mat on the intake vent.\n3. Verify enclosure door seals are intact and door is fully closed.\n4. Check shop HVAC setpoint and confirm the system is running.\n5. Measure ambient shop temperature — if above 35°C, pause production until cooling is restored.\n6. Check servo drive and PLC temperatures in the diagnostic screen.\n7. Schedule filter mat replacement and fan motor inspection.",
  },
  torque: {
    sensor_description:
      "torque (N·m) is measured at the spindle drive motor and represents the cutting resistance load. High threshold: 75 N·m. Critical threshold: 110 N·m.",
    what_happens_physically:
      "Excess torque strains spindle bearings, overloads the drive belt or coupling, and can trigger thermal shutdown of the servo amplifier. Sustained over-torque accelerates bearing raceway fatigue. Sudden torque spikes (>110 N·m) indicate tool dig-in or material inclusion which can snap the tool and cause uncontrolled spindle stop.",
    typical_root_causes:
      "Primary cause: tool wear increasing cutting forces (check toolWear). Secondary causes: programmed feed rate or depth-of-cut too aggressive for the workpiece material; incorrect spindle speed for the tool diameter; workpiece fixture slip causing irregular engagement; spindle bearing pre-load too tight after recent service.",
    inspection_steps:
      "1. Record the torque value and timestamp for maintenance log.\n2. Check current tool wear — worn tools are the #1 cause of torque rise.\n3. Verify G-code feed rate and depth-of-cut against the tooling spec.\n4. Inspect drive belt tension and check for glazing or cracking.\n5. Listen for bearing rumble during low-speed spindle rotation.\n6. Check servo amplifier fault log for over-current events.\n7. Reduce feed rate by 20% and retest before returning to full speed.",
  },
  toolWear: {
    sensor_description:
      "toolWear measures cumulative cutting minutes since last tool change. Normal operating range is 0–150 min. High threshold: 150 min. Critical threshold: 230 min.",
    what_happens_physically:
      "As tool edges dull, cutting forces increase and surface finish degrades. Worn tools generate more frictional heat at the cutting zone. Beyond 230 min, tool fracture risk increases sharply, which can jam the spindle or damage the workpiece fixture.",
    typical_root_causes:
      "Primary cause: scheduled tool-change interval was missed or overridden. Secondary causes: harder-than-expected workpiece material; incorrect feed-rate or depth-of-cut programmed in G-code; insufficient coolant at the cutting zone accelerating edge degradation.",
    inspection_steps:
      "1. Stop spindle rotation before any inspection.\n2. Visually inspect the cutting edge under magnification for chipping, flank wear land, or built-up edge (BUE).\n3. Measure flank wear with a tool-wear gauge — replace if >0.3 mm VB.\n4. Check the tool holder collet for runout with a dial indicator.\n5. After replacement, verify coolant nozzle is aimed at the cutting zone.\n6. Update the tool-life counter in the CNC controller.\n7. Run a test cut and inspect surface finish before resuming production.",
  },
  rotationalSpeedLow: {
    sensor_description:
      "rotationalSpeed (RPM) is the spindle encoder speed feedback. Low-speed warning triggers below 1000 RPM when non-zero. Critical trigger below 600 RPM. A value of 0 RPM during a commanded cut indicates a stall condition.",
    what_happens_physically:
      "Below-spec RPM causes incorrect surface cutting speed (SFM), leading to built-up edge (BUE) on the tool and poor surface finish. If RPM drops during a cut, the feed-per-tooth increases beyond design limits, risking tool breakage. A complete stall can strip the drive belt or trigger the spindle motor thermal overload.",
    typical_root_causes:
      "Primary cause: drive belt worn, glazed, or slipping on pulley. Secondary causes: spindle motor brushes worn (DC motors); spindle bearing seizure increasing drag; VFD (variable frequency drive) parameter drift; insufficient lubrication in the gearbox or spindle head; mechanical drag from a crashed or jammed tool holder.",
    inspection_steps:
      "1. Command a no-load spindle run and observe RPM feedback vs setpoint.\n2. Inspect drive belt — check tension, look for glazing, cracking, or missing teeth on toothed belts.\n3. Check belt pulley alignment with a straightedge.\n4. Verify VFD output frequency matches the RPM command.\n5. Check spindle motor current draw — high current at low RPM indicates mechanical drag.\n6. Lubricate spindle head per OEM schedule.\n7. Check and re-tension belt to OEM torque spec.",
  },
  rotationalSpeedHigh: {
    sensor_description:
      "rotationalSpeed (RPM) at the high end. Warning above 3000 RPM. Critical above 3500 RPM beyond rated spindle speed.",
    what_happens_physically:
      "Over-speed causes centrifugal stress on the spindle shaft and tool holder, increasing the risk of collet slip and tool ejection. Bearing dynamic load increases with the square of RPM, rapidly reducing bearing life. Coolant at high RPM does not penetrate the cutting zone effectively, increasing thermal load.",
    typical_root_causes:
      "Primary cause: CNC controller spindle override manually set above 100%. Secondary causes: VFD max-frequency parameter incorrectly configured; runaway feedback loop in the spindle servo drive; incorrect S-word (speed command) in the part program for the current tool.",
    inspection_steps:
      "1. Immediately verify spindle override percentage at the operator panel — reset to 100% if elevated.\n2. Check VFD parameter P-max (maximum output frequency) against OEM spec.\n3. Review the active part program S-word commands.\n4. Inspect spindle servo drive fault log for runaway events.\n5. Check tool holder retention knob torque — over-speed may have caused micro-slip in the collet.\n6. Perform a bearing condition check (vibration or temperature).",
  },
  nullRpmRatio: {
    sensor_description:
      "nullRpmRatio is the fraction of sensor readings where rotationalSpeed is NULL (not zero — missing entirely). A null reading means the encoder signal was not received by the controller, not that the spindle stopped. High threshold: 40% null. Critical threshold: 60% null.",
    what_happens_physically:
      "Loss of encoder signal means the CNC controller loses closed-loop speed control. The drive may default to open-loop, causing speed instability. If the controller is configured to fault on signal loss, intermittent nulls cause nuisance E-stops. No speed data also means that torque and tool-wear calculations depending on RPM become unreliable.",
    typical_root_causes:
      "Primary cause: encoder cable connector loose, corroded, or damaged (look for coolant intrusion into the connector shell). Secondary causes: encoder disk cracked or contaminated with swarf; cable routed too close to the spindle motor causing EMI interference; encoder power supply voltage below spec (should be +5V ±5%); controller encoder input card fault.",
    inspection_steps:
      "1. Stop the spindle and lock out the machine (LOTO).\n2. Locate the spindle encoder connector — typically at the rear of the spindle head. Clean and re-seat the connector.\n3. Inspect the cable from encoder to controller for chafing, pinching, or coolant saturation.\n4. Measure encoder supply voltage at the connector — must be 4.75–5.25V.\n5. Inspect the encoder disk through the access port for cracks or contamination; clean with lint-free cloth if contaminated.\n6. Check cable shielding continuity and verify the shield is grounded at one end only to prevent ground loops.\n7. After repair, run a signal integrity test at multiple RPM setpoints and confirm null ratio drops to <1%.",
  },
};

const FIELD_LABELS = {
  sensor_description:      "Sensor Description",
  what_happens_physically: "Sensor Critical Impact",
  typical_root_causes:     "Typical Root Causes",
  inspection_steps:        "Inspection Steps",
};

// ── Knowledge Dialog ──────────────────────────────────────────────────────────
const KnowledgeDialog = ({ open, sensor, onClose }) => {
  const [fields, setFields] = useState({});

  // Initialise local state when sensor changes
  React.useEffect(() => {
    if (sensor) setFields({ ...SENSOR_KNOWLEDGE[sensor.key] });
  }, [sensor]);

  if (!sensor) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={700}>
            {sensor.label} — Sensor Instructions
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Edit the knowledge base entries used by the AI diagnostic engine.
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: "text.secondary" }}>
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ overflowY: "auto" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {Object.keys(FIELD_LABELS).map((field) => (
            <TextField
              key={field}
              label={FIELD_LABELS[field]}
              value={fields[field] ?? ""}
              disabled
              multiline
              minRows={field === "inspection_steps" ? 7 : 3}
              fullWidth
              size="small"
              sx={{
                "& .MuiOutlinedInput-root.Mui-disabled": {
                  bgcolor: "#FAFAFA",
                  "& fieldset": { borderColor: "#D1D5DB" },
                },
                "& .Mui-disabled": { WebkitTextFillColor: "#374151 !important" },
                "& .MuiInputLabel-root.Mui-disabled": { color: "#6B7280" },
              }}
            />
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" size="small">
          Cancel
        </Button>
        <Button
          disabled
          variant="contained"
          size="small"
          sx={{ bgcolor: "#1B2A3D", "&:hover": { bgcolor: "#243548" } }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Page ───────────────────────────────────────────────────────────────────────
const ManageThresholdsPage = () => {
  const [dialogSensor, setDialogSensor] = useState(null);

  return (
    <Box sx={{ bgcolor: "#F8F9FB", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 } }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} color="text.primary">
            Manage Thresholds
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Operational thresholds and AI knowledge base used by the CNC diagnostic engine.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", p: 3, bgcolor: "background.paper" }}
        >
          {/* Column headers */}
          <Grid container spacing={2} sx={{ mb: 1.5, px: 0.5 }}>
            <Grid size={{ xs: 12, sm: 4 }} />
            <Grid size={{ xs: 5, sm: 3 }}>
              <Typography variant="caption" fontWeight={700} color="#92400E" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                High
              </Typography>
            </Grid>
            <Grid size={{ xs: 5, sm: 3 }}>
              <Typography variant="caption" fontWeight={700} color="#991B1B" sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}>
                Critical
              </Typography>
            </Grid>
            <Grid size={{ xs: 2, sm: 2 }} />
          </Grid>

          {THRESHOLDS.map((sensor) => (
            <Grid container spacing={2} key={sensor.key} sx={{ mb: 2, alignItems: "center" }}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <Typography variant="body2" fontWeight={500} color="text.primary">
                  {sensor.label}
                </Typography>
              </Grid>

              <Grid size={{ xs: 5, sm: 3 }}>
                <TextField
                  value={sensor.high}
                  disabled
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" color="text.secondary">{sensor.unit}</Typography>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { bgcolor: "#FFFBEB", "& fieldset": { borderColor: "#F59E0B" } },
                    "& .Mui-disabled": { WebkitTextFillColor: "#92400E !important" },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 5, sm: 3 }}>
                <TextField
                  value={sensor.critical}
                  disabled
                  size="small"
                  fullWidth
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography variant="caption" color="text.secondary">{sensor.unit}</Typography>
                        </InputAdornment>
                      ),
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": { bgcolor: "#FEF2F2", "& fieldset": { borderColor: "#EF4444" } },
                    "& .Mui-disabled": { WebkitTextFillColor: "#991B1B !important" },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 2, sm: 2 }} sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setDialogSensor(sensor)}
                  startIcon={<SlidersHorizontal size={14} />}
                  sx={{
                    fontSize: 11,
                    textTransform: "none",
                    borderColor: "divider",
                    color: "text.secondary",
                    "&:hover": { borderColor: "#1B2A3D", color: "#1B2A3D" },
                  }}
                >
                  Instructions
                </Button>
              </Grid>
            </Grid>
          ))}
        </Paper>

        {/* ML Endpoint Threshold */}
        <Paper
          elevation={0}
          sx={{ borderRadius: 3, border: "1px solid #C7D2FE", p: 3, bgcolor: "#EEF2FF", mt: 3 }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <AutoAwesomeIcon sx={{ fontSize: 20, color: "#4F46E5" }} />
            <Typography variant="subtitle1" fontWeight={700} color="#1E1B4B">
              ML Endpoint Threshold
            </Typography>
            <Chip
              label="Automated Agent"
              size="small"
              sx={{ height: 20, fontSize: "0.6875rem", fontWeight: 600, bgcolor: "#E0E7FF", color: "#4F46E5" }}
            />
          </Box>

          <Grid container spacing={2} sx={{ alignItems: "center" }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Typography variant="body2" fontWeight={500} color="#1E1B4B">
                Failure Probability
              </Typography>
              <Typography variant="caption" color="#6366F1" sx={{ display: "block", mt: 0.25 }}>
                Beyond this threshold the automated agent will create a work order
              </Typography>
            </Grid>

            <Grid size={{ xs: 5, sm: 3 }}>
              <TextField
                value={70}
                disabled
                size="small"
                fullWidth
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <Typography variant="caption" color="text.secondary">%</Typography>
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { bgcolor: "#E0E7FF", "& fieldset": { borderColor: "#6366F1" } },
                  "& .Mui-disabled": { WebkitTextFillColor: "#3730A3 !important" },
                }}
              />
            </Grid>

            <Grid size={{ xs: 7, sm: 5 }}>
              <Typography variant="caption" color="#4338CA" sx={{ lineHeight: 1.6 }}>
                When the ML model predicts a failure probability ≥ 70%, the AI orchestrator
                triggers the full diagnostic pipeline — root-cause analysis, work order creation,
                technician assignment, and risk scoring.
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Footer />

      <KnowledgeDialog
        open={Boolean(dialogSensor)}
        sensor={dialogSensor}
        onClose={() => setDialogSensor(null)}
      />
    </Box>
  );
};

export default ManageThresholdsPage;
